// internal-deps
import React, { memo, useCallback, useContext, useMemo } from "react";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import { SnaccTheme } from "@swiggy-private/dls-web-common/dist/components/themes/Snacc";

// components
import { StealDealWidget } from "@swiggy-private/cart-ui";
import { ErrorBoundary } from "@swiggy-private/core-ui";
// types
import { CartItemWithUUID, StealDealWidgetRenderingDetails } from "@swiggy-private/cart-types";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
// contexts
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartToastContext } from "@cart-client/contexts/CartToastContext";

//hooks
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";

// helpers
import { createCartItemListWithUUID } from "@cart-client/helpers/CartItemRenderer";
import { isEmpty } from "@swiggy-private/js-utils";
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";
interface StealDealWidgetRendererProps {
    widgetData: StealDealWidgetRenderingDetails;
    cartResponse: ReviewedCart;
}

function StealDealWidgetRenderer({
    widgetData,
    cartResponse,
}: StealDealWidgetRendererProps): React.ReactElement | null {
    const { onImpressionStealDealWidget, onClickStealDealAddButton } = useCartRendererAnalytics(cartResponse);
    const { doUpdateCart } = useContext(UpdateCartContext);
    const {
        cartState: { localCart },
    } = useContext(CartContext);
    const { setCartToastData } = useContext(CartToastContext);
    const { cartId, originalCartId } = cartResponse?.data || {};

    const itemsListWithUUID = useMemo(
        () => createCartItemListWithUUID(widgetData?.meta?.items || []),
        [widgetData?.meta?.items],
    );

    const { isSnaccStorefrontV2 } = useCartRendererXP(cartResponse);

    const handleAddStealDeal = useCallback(
        async (item: CartItemWithUUID) => {
            const isUnlocked = widgetData?.meta?.movMet || false;
            if (!isUnlocked) return;
            try {
                await doUpdateCart({
                    ...localCart,
                    cartItems: localCart?.cartItems?.concat([{ ...item, quantity: 1 }]),
                });
            } catch (error) {
                setCartToastData({
                    message: "Failed to Add steal deal item. Please try again.",
                    show: true,
                });
            }
        },
        [widgetData?.meta?.movMet, doUpdateCart, localCart, setCartToastData],
    );

    const handleImpression = useCallback(() => {
        onImpressionStealDealWidget(
            String(cartId),
            String(originalCartId),
            itemsListWithUUID,
            widgetData?.meta?.movMet || false,
        );
    }, [cartId, originalCartId, itemsListWithUUID, widgetData?.meta?.movMet, onImpressionStealDealWidget]);

    const handleAddStealDealEvent = useCallback(
        (item: CartItemWithUUID) => {
            onClickStealDealAddButton(String(cartId), String(originalCartId), item);
        },
        [cartId, originalCartId, onClickStealDealAddButton],
    );

    if (isEmpty(widgetData?.meta)) return null;
    return (
        <ErrorBoundary errorContent={null}>
            <ThemeProvider theme={SnaccTheme} colorMode="light">
                <StealDealWidget
                    widgetData={widgetData?.meta}
                    items={itemsListWithUUID}
                    onAddStealDeal={handleAddStealDeal}
                    onImpression={handleImpression}
                    onAddStealDealEvent={handleAddStealDealEvent}
                    isSnaccStorefrontV2={isSnaccStorefrontV2}
                />
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default memo(StealDealWidgetRenderer);
